import {
  ApplicationStatus,
  ContractorType,
  MapServiceListItem,
  Role
} from 'src/app/models';
import { Client } from './client';
import { CommunityWorkflow } from './community-workflow';
import { Securable } from './securable';
import { Permission } from './permission';
import { WorkflowVersion } from './workflow-version';
import { WorkflowTag } from './workflow-tag';
import { ConditionTarget } from '../components/system/condition-builder/condition-builder.model';
import { Requirement } from './requirement';
import { CustomList, CustomListItem } from './custom-list';
import { FeeRecipient } from './fee-recipient';
import { EmailFooter } from './email-footer';
import { FeeDataEntity } from './data-entities';
import { EmailActivity, PrintTemplateActivity } from './activities';
import { ContractorSelectionActivity } from './activities/contractor-selection-activity';
import { SelectContractorDataEntity } from './data-entities/select-contractor-data-entity';
import { WorkflowCategory } from './workflow-category';
import { CustomReportFilterInput } from './custom-report';

export interface DepartmentApplicationsRequest {
  departmentId: string;
  filterInput?: CustomReportFilterInput;
  columnsToInclude?: string[];
  sortField?: string;
  sortDescending?: boolean;
  ignoreShowInApplications?: boolean;
}

export enum WorkflowType {
  Permit,
  ContractorRegistration,
  ContractorRenewal,
  Other,
  Renewable,
  MobileInspection
}

export class AutoVoidSettings {
  goBeyondActivity: string;
  daysUntilVoid: number;
}

export class Workflow implements Securable {
  id: string;
  client: Client;
  clientID: string;
  isTemplateLibraryWorkflow: boolean;
  communityWorkflow?: CommunityWorkflow;
  permissions: { [key: string]: { [key: string]: Permission } };
  isArchived: boolean;
  isEnabled: boolean;
  attachments: string[];
  visibleToPublic: boolean;
  version: WorkflowVersion;
  isPublished: boolean;
  clientApplicationNumber: number;
  numberPrefix: string;
  numberPrefixCriteria: ConditionTarget[];
  workflowType: WorkflowType;
  areThereChangesNotPublished: boolean;
  lastPublishedOnString: string;
  lastPublishedBy: string;
  workflowTags: WorkflowTag[];
  categories: WorkflowCategory[];
  name: string;
  designStatus: ApplicationStatus; // PER2-1340 this is a client only setting that is used in the designer instead of having to pass this around everywhere :)
  expirationFormulaCriteria: ConditionTarget[];
  parentWorkflowid?: string;
  renewalWorkflowId?: string;
  requirements?: Requirement[];
  fixedClientApplicationNumberLength?: number;
  resetClientAppNumberMonth?: number;
  resetClientAppNumberDay?: number;
  nextClientAppNumberResetDateText?: string;
  autoVoidSettings: AutoVoidSettings;
  canPublish: boolean;
  adminRole: Role;
  validationWarningsOnly: boolean;
  canMakePublic: boolean;
  showInApplications: boolean;

  availableActions: string[];

  constructor(options?: Partial<Workflow>) {
    this.version = new WorkflowVersion();
    if (options) {
      Object.assign(this, options);
    }
  }
}

export class RenewableParentsResponse {
  renewableParentIds: string[];
}

export class TemplateWorkflowListItem {
  id: string;
  workflowVersionId: string;
  name: string;
  description: string;
  modifiedOnString: string;
  tags: string[];
}

export class TemplateWorkflowSearchRequest {
  name: string;
  tags: string[];

  constructor(options?: Partial<TemplateWorkflowSearchRequest>) {
    if (options) {
      Object.assign(this, options);
    }
  }
}

export class TemplateWorkflowSearchResponse {
  items: TemplateWorkflowListItem[];
}

export class WorkflowCopyConfiguration {
  action: WorkflowConfigurationSaveAction;
  sourceWorkflowVersionId: string;
  workflowUpdates: WorkflowSettingsConfiguration;
  customListMappings: CustomListMappings;
  roleMappings: RoleMappings;
  feeRecipientMappings: FeeRecipientMappings;
  emailFooterMappings: EmailFooterMappings;
  feeUpdates: FeeUpdates;
  emailUpdates: EmailUpdates;
  mapServiceConfiguration: MapServiceConfiguration;
  printTemplateUpdates: PrintTemplateUpdates;
  selectContractorUpdates: SelectContractorUpdates;
}

export class Section {
  key: string;
  label: string;
  enabled: boolean;
  completed: boolean;
}

export enum WorkflowConfigurationSaveAction {
  Import = 1,
  Copy = 2
}

export class WorkflowConfigurationSaveListItem {
  id: string;
  sourceWorkflowId: string;
  sourceWorkflowName: string;
  action: WorkflowConfigurationSaveAction;
  detailedAction: string;
  createdBy: string;
  modifiedBy: string;
}

export class RoleMappings {
  completed: boolean;
  mappings: RoleMapping[];
}

export class RoleMapping {
  sourceRoleId: string;
  sourceRoleName: string;
  targetRoleId: string;
}

export class CustomListMappings {
  completed: boolean;
  mappings: CustomListMapping[];
}

export class CustomListMapping {
  sourceListId: string;
  sourceListName: string;
  sourceRoleName: string;
  targetListId: string; // If Target List Id is null then the entire list gets added to the target destination
  targetRoleId: string;
  itemMappings: CustomListItemMapping[];
}

export class CustomListItemMapping {
  sourceItem: CustomListItem;
  targetItemId: string; // If TargetItemId is null then the item is getting added to the list
}

export class WorkflowSettingsConfiguration {
  completed: boolean;
  name: string;
  description: string;
}

export class WorkflowNumberSettings {
  prefix: ConditionTarget[];
  startingNumber?: number;
  fixedNumberLength?: number;
  autoResetMonth?: number;
  autoResetDay?: number;
}

export class FeeRecipientMappings {
  completed: boolean;
  mappings: FeeRecipientMapping[];
}

export class FeeRecipientMapping {
  sourceId: string;
  sourceValue: string;
  targetId: string;
  isIgnored: boolean; // If this is true, we will clear out the fee recipient on the fees that use the source Id recipient
}

export class EmailFooterMappings {
  completed: boolean;
  mappings: EmailFooterMapping[];
}

export class EmailFooterMapping {
  sourceEmailFooter: EmailFooter;
  targetId: string;
  isIgnored: boolean; // If this is true, we will create it in the destination and we will disconnect it from any associate email activities
  targetEmailFooterContent: string;
}

export class FeeUpdates {
  completed: boolean;
  feeDataEntities: FeeDataEntity[];
}

export class EmailUpdates {
  completed: boolean;
  emailActivities: EmailActivity[];
  emailSections: Section[];
}

export class SelectContractorUpdates {
  completed: boolean;
  contractorSelectionActivities: ContractorSelectionActivity[];
  selectContractorDataEntities: SelectContractorDataEntity[];
}

export class PrintTemplateUpdates {
  completed: boolean;
  printTemplateActivities: PrintTemplateActivity[];
}

export class MapServiceConfiguration {
  completed: boolean;
  targetId: string;
}

export class PaymentAccountConfiguration {
  targetId: string;
}

export class WorkflowCopyOptions {
  sourceWorkflowElements: WorkflowCopyConfiguration;
  targetJurisdictionResources: TargetJurisdictionResources;
  mustCreateContractorType: boolean;
  mustConfigureMapService: boolean;
  mustConfigureParcelDataSource: boolean;
  originalSourceWorkflowName: string;
}

export class TargetJurisdictionResources {
  targetJurisdictionId: string;
  responsibleRoles: Role[];
  rolesForCustomLists: Role[];
  customLists: CustomList[];
  feeRecipients: FeeRecipient[];
  emailFooters: EmailFooter[];
  mapServices: MapServiceListItem[];
}
